import { useState, useCallback, useRef } from "react";
import { api } from "@/lib/api-client";

interface CheckResponse {
  allowed: boolean;
}

interface PermissionCache {
  [key: string]: boolean;
}

function cacheKey(action: string, stage?: string, jobTypeId?: string): string {
  return `${action}:${stage ?? "*"}:${jobTypeId ?? "*"}`;
}

export function usePermissions() {
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef<PermissionCache>({});
  const pendingRef = useRef<Map<string, Promise<boolean>>>(new Map());
  const [, forceUpdate] = useState(0);

  const checkPermission = useCallback(
    async (
      action: string,
      stage?: string,
      jobTypeId?: string,
    ): Promise<boolean> => {
      const key = cacheKey(action, stage, jobTypeId);

      // Return from cache if available
      const cached = cacheRef.current[key];
      if (cached !== undefined) {
        return cached;
      }

      // Deduplicate in-flight requests
      if (pendingRef.current.has(key)) {
        return pendingRef.current.get(key)!;
      }

      setIsLoading(true);

      const promise = (async () => {
        try {
          const qs = new URLSearchParams({ action });
          if (stage) qs.set("stage", stage);
          if (jobTypeId) qs.set("jobTypeId", jobTypeId);

          const result = await api.get<CheckResponse>(
            `/roles/permissions/check?${qs.toString()}`,
          );

          cacheRef.current[key] = result.allowed;
          forceUpdate((n) => n + 1);
          return result.allowed;
        } catch {
          // On error, default to no permission
          cacheRef.current[key] = false;
          return false;
        } finally {
          pendingRef.current.delete(key);
          setIsLoading(false);
        }
      })();

      pendingRef.current.set(key, promise);
      return promise;
    },
    [],
  );

  const hasPermission = useCallback(
    (action: string, stage?: string, jobTypeId?: string): boolean => {
      const key = cacheKey(action, stage, jobTypeId);
      const cached = cacheRef.current[key];
      return cached === true;
    },
    [],
  );

  return { checkPermission, hasPermission, isLoading };
}
